import debounce from "debounce";
import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import Icon, { ICON_NAME } from "../../../../../components/Icon";
import LoadingBox from "../../../../../components/LoadingBox";
import CompleteStep from "../../../../../components/Operation/Steps/CompleteStep";
import SiteStep from "../../../../../components/Operation/Steps/SiteStep";
import { OpService } from "../../../../../util/services/operation";
import {
    ACTION_TYPE, OPERATION_TYPE
} from "../../../../../util/varibles/constants";
import { showErrorResponse } from "../../../../../util/varibles/global";
import { ITaskService } from "../../../../../util/varibles/interface";
import { getActivityLogDurationEst, paramsActivityLog } from "../../../util";
import { resolveActivityLog } from "../../../util/function_operation/global";
import { getEditOp, useEditOp } from "../EditOperationContext";
import styles from "./style.module.scss";

const Loading = () => {
    const [loadingSubmit] = useEditOp(state => state.loadingSubmit);

    return loadingSubmit ? <LoadingBox className='z-2' loading={true} level='bg' /> : null
}

interface IProps {
    tasks: ITaskService[]
    children?: ReactNode
    trackDurationEst?: boolean
}

const ActivityLog: React.FC<IProps> = ({ tasks, children, trackDurationEst = false }) => {
    const [startTime, set] = useEditOp(state => state.startTime);
    const [activity_log] = useEditOp(state => state.activity_log);
    const get = getEditOp();
    const signal = useRef(new AbortController());
    const { location_name } = tasks[0] || {};
    const time = useMemo(() => tasks.reduce((total: number, item) => item.type ? total + item.duration : total, 0), [tasks]);

    const waitActivityLog = useRef(debounce((startTime: number) => {
        const { vessel, operation, isCalculate, activity_log, editMode } = get();
        if (!vessel || !operation || !isCalculate)
            return;
        const { param } = paramsActivityLog[OPERATION_TYPE.SERVICE]({
            operation,
            vessel
        }, { property: { start: startTime } });
        if (signal.current)
            signal.current.abort();
        signal.current = new AbortController();
        set({ loadingSubmit: true })
        const controller = signal.current;
        const baseline = trackDurationEst && editMode !== ACTION_TYPE.CREATE
            ? getActivityLogDurationEst({ operation, vessel, activity_log }, controller, true)
            : Promise.resolve(operation.duration_est);
        baseline
            .then(async durationEst => {
                const rs = await OpService.activityLog(param, controller);
                set(({ operation: prevOp, activity_log: currentLog }) => {
                    const resolved = trackDurationEst
                        ? resolveActivityLog(currentLog, rs.activity_log, durationEst)
                        : { activity_log: rs.activity_log, duration_est: undefined };
                    const log = resolved.activity_log;
                    const start = log[0];
                    const finish = log[log.length - 1];
                    return {
                        activity_log: log,
                        loadingSubmit: false,
                        operation: prevOp ? {
                            ...prevOp,
                            ...(trackDurationEst ? { duration_est: resolved.duration_est } : {}),
                            duration: finish?.est_start_time - start?.est_start_time
                        } : prevOp
                    };
                })
            })
            .catch(e => {
                if (e.name !== 'AbortError') {
                    showErrorResponse('Error', e);
                }
                set({ loadingSubmit: false })
            });
    }, 360));

    useEffect(() => {
        if (time)
            waitActivityLog.current(startTime);

    }, [time, startTime]);

    const start = activity_log[0];
    const finish = activity_log[activity_log.length - 1];

    return <>{(start && finish) && <div className={styles.activityLog}>
        <Loading />
        <SiteStep data={start} locationName={location_name} />
        {children}
        <Icon className="mt-2" icon={ICON_NAME.ARROW_DOWN_3} bg="colorDisable" />
        <Icon className='mt-[-2px] mb-3' icon={ICON_NAME.ARROW_DOWN_3} bg="colorDisable" />
        <CompleteStep data={finish} />
    </div>}
    </>;
};

export default ActivityLog;
